import React, { useState } from 'react';
import swPaintColors from './swPaintColors';

function PaintColorSelector({ label, value, onChange, disabled, required = false }) {
  const [showColors, setShowColors] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Function to convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Function to automatically categorize color by hex code
  const getColorCategory = (hexCode) => {
    const rgb = hexToRgb(hexCode);
    if (!rgb) return 'Other';

    const { r, g, b } = rgb;
    
    // Calculate brightness (0-255)
    const brightness = (r + g + b) / 3;
    
    // Calculate saturation
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const saturation = max === 0 ? 0 : (max - min) / max;

    // Very light colors (near white)
    if (brightness > 240 && saturation < 0.1) {
      return 'White';
    }

    // Very dark colors (near black)
    if (brightness < 40) {
      return 'Black';
    }

    // Gray (low saturation, medium brightness)
    if (saturation < 0.15 && brightness >= 40 && brightness <= 240) {
      return 'Gray';
    }

    // Beige/Tan (low saturation, warmer tones)
    if (saturation < 0.3 && r > g && g > b && brightness > 100) {
      return 'Beige';
    }

    // Brown (darker, low saturation, warm)
    if (saturation < 0.4 && r > g && g >= b && brightness < 140) {
      return 'Brown';
    }

    // Now check for chromatic colors (higher saturation)
    const hue = Math.atan2(Math.sqrt(3) * (g - b), 2 * r - g - b) * (180 / Math.PI);
    const normalizedHue = hue < 0 ? hue + 360 : hue;

    // Red: 0-20° and 340-360°
    if ((normalizedHue >= 0 && normalizedHue <= 20) || normalizedHue >= 340) {
      return 'Red';
    }
    
    // Orange: 20-45°
    if (normalizedHue > 20 && normalizedHue <= 45) {
      return 'Orange';
    }
    
    // Yellow: 45-70°
    if (normalizedHue > 45 && normalizedHue <= 70) {
      return 'Yellow';
    }
    
    // Green: 70-160°
    if (normalizedHue > 70 && normalizedHue <= 160) {
      return 'Green';
    }
    
    // Blue: 160-260°
    if (normalizedHue > 160 && normalizedHue <= 260) {
      return 'Blue';
    }
    
    // Purple/Violet: 260-300°
    if (normalizedHue > 260 && normalizedHue <= 300) {
      return 'Purple';
    }
    
    // Pink/Magenta: 300-340°
    if (normalizedHue > 300 && normalizedHue < 340) {
      return 'Pink';
    }

    return 'Other';
  };

  // Add category to each color automatically
  const colorsWithCategories = swPaintColors.map(color => ({
    ...color,
    category: getColorCategory(color.code)
  }));

  const categorizeColors = (colors) => {
    const categories = {};
    colors.forEach(color => {
      if (!categories[color.category]) {
        categories[color.category] = [];
      }
      categories[color.category].push(color);
    });
    return categories;
  };

  const categorizedColors = categorizeColors(colorsWithCategories);
  const categories = Object.keys(categorizedColors).sort();

  const handleColorSelect = (colorName) => {
    onChange(colorName);
    setShowColors(false);
    setSelectedCategory(null);
  };

  const selectedColor = colorsWithCategories.find(c => c.name === value);

  return (
    <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #dee2e6' }}>
      <div 
        onClick={() => setShowColors(!showColors)}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div>
          <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: '600', color: '#333' }}>
            {label}
            {required && <span style={{ color: '#dc3545', marginLeft: '0.25rem' }}>*</span>}
            {selectedColor && (
              <span style={{ color: '#007bff', fontSize: '0.9rem', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                ({selectedColor.name})
              </span>
            )}
          </h4>
          {selectedColor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
              <div style={{
                width: '24px',
                height: '24px',
                backgroundColor: selectedColor.code,
                border: '2px solid #ddd',
                borderRadius: '4px'
              }} />
              <span style={{ fontSize: '0.85rem', color: '#666' }}>{selectedColor.name}</span>
              {/* <span style={{ fontSize: '0.75rem', color: '#999', fontStyle: 'italic' }}>({selectedColor.category})</span> */}
            </div>
          )}
          {!selectedColor && (
            <p style={{ fontSize: '0.9rem', color: '#666', margin: '0.25rem 0 0 0' }}>
                Choose a Sherwin Williams paint color
            </p>
          )}
          
        </div>
        <span style={{ fontSize: '1.5rem', color: '#666' }}>
          {showColors ? '⌄' : '^'}
        </span>
      </div>
      
      {showColors && (
        <>
          {/* Category Selection */}
          {!selectedCategory && (
            <div style={{ marginTop: '1rem' }}>
              <h5 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#666' }}>Select a color family:</h5>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                gap: '0.75rem'
              }}>
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    disabled={disabled}
                    style={{
                      padding: '0.75rem',
                      backgroundColor: '#f8f9fa',
                      border: '2px solid #dee2e6',
                      borderRadius: '8px',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#333',
                      transition: 'all 0.2s',
                      opacity: disabled ? 0.6 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!disabled) {
                        e.currentTarget.style.backgroundColor = '#e3f2fd';
                        e.currentTarget.style.borderColor = '#007bff';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!disabled) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.borderColor = '#dee2e6';
                      }
                    }}
                  >
                    {category} ({categorizedColors[category].length})
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Color Selection within Category */}
          {selectedCategory && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <button
                  onClick={() => setSelectedCategory(null)}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  ← Back
                </button>
                <h5 style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                  {selectedCategory} Colors ({categorizedColors[selectedCategory].length}):
                </h5>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: '1rem'
              }}>
                {categorizedColors[selectedCategory].map(color => (
                  <div
                    key={color.name}
                    onClick={() => {
                      if (!disabled) {
                        handleColorSelect(color.name);
                      }
                    }}
                    style={{
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      textAlign: 'center',
                      opacity: disabled ? 0.6 : 1,
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        height: '80px',
                        backgroundColor: color.code,
                        borderRadius: '8px',
                        border: value === color.name ? '3px solid #007bff' : '2px solid #ddd',
                        boxShadow: value === color.name ? '0 4px 12px rgba(0,123,255,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s',
                        transform: value === color.name ? 'scale(1.05)' : 'scale(1)',
                        position: 'relative'
                      }}
                    >
                      {value === color.name && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: 'bold',
                          }}
                        >
                          ✓
                        </div>
                      )}
                    </div>
                    <p style={{
                      margin: '0.5rem 0 0 0',
                      fontSize: '0.85rem',
                      color: value === color.name ? '#007bff' : '#333',
                      fontWeight: value === color.name ? '600' : '400',
                    }}>
                      {color.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {disabled && (
            <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#dc3545', fontStyle: 'italic' }}>
              Paint color selection is locked. Contact administrator to make changes.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default PaintColorSelector;